/**
 * directorySearchParams — the directory's search state in its URL, both
 * ways: `searchStateFromParams` reads it at mount, `writeSearchState`
 * writes it back on every change. The two live together so a key one
 * of them knows and the other does not is caught by one round-trip test
 * (tests/ui/directoryBackFilters.test.tsx).
 *
 * A multi-pick filter is one parameter holding its values joined with
 * commas. A value can itself hold a comma: a county's display name is
 * "Atlantic County, NJ". So each value escapes its own commas (and its
 * percent signs, so an escape reads back as the value that made it) as
 * %2C and %25 before the join. A value with neither reads exactly as it
 * did before this encoding, so a URL written earlier still works.
 *
 * URLs written before the encoding joined county names raw, giving
 * "Atlantic County, NJ,Cape May County, NJ". The writer never put a
 * space after its own comma, so a piece that starts with whitespace is
 * the rest of the value before it, and the reader joins it back. For
 * that to hold, a value that itself starts with whitespace escapes it
 * (%20, %09, %0A, %0D), so a raw leading space only ever comes from an
 * older URL.
 */
import type { DirectoryFilters } from '@/components/peace-hub-port/FilterBar';
import { SORT_OPTIONS } from '@/components/peace-hub-port/SortControl';

/** Every multi-pick filter the directory has, each under its own key. */
export const DIRECTORY_LIST_FILTER_KEYS = [
  'categories',
  'subcategories',
  'specialties',
  'counties',
  'highlights',
  'service_models',
  'payment_types',
  'contact_specialties',
] as const;

/** The on/off filters and the parameter each one is written under. */
const DIRECTORY_BOOL_FILTER_PARAMS = [
  ['has_notes', 'has_notes'],
  ['recommended_only', 'recommended'],
  ['is_referral_partner', 'is_referral_partner'],
] as const;

export interface DirectorySearchState {
  query: string;
  viewMode: string;
  filters: DirectoryFilters;
  sortMode: string;
  visibleCount: number;
}

const hex = (c: string): string => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`;

/** One multi-pick filter's values, as its parameter holds them. */
export function encodeListParam(values: readonly string[]): string {
  return values
    .map((v) => v.replace(/%/g, '%25').replace(/,/g, '%2C').replace(/^[ \t\r\n]+/, (lead) => [...lead].map(hex).join('')))
    .join(',');
}

/** A multi-pick parameter's values, in the encoded form or the older raw one. */
export function decodeListParam(raw: string): string[] {
  const pieces: string[] = [];
  for (const piece of raw.split(',')) {
    if (pieces.length > 0 && /^[ \t\r\n]/.test(piece)) pieces[pieces.length - 1] += `,${piece}`;
    else pieces.push(piece);
  }
  return pieces
    .map((p) => p.replace(/%(2C|25|20|09|0A|0D)/gi, (_m, code: string) => String.fromCharCode(parseInt(code, 16))))
    .filter(Boolean);
}

/** The search state a directory URL names. */
export function searchStateFromParams(sp: URLSearchParams): DirectorySearchState {
  const filters: DirectoryFilters = {};
  for (const key of DIRECTORY_LIST_FILTER_KEYS) {
    const raw = sp.get(key);
    if (raw) {
      const values = decodeListParam(raw);
      if (values.length > 0) filters[key] = values;
    }
  }
  for (const [key, param] of DIRECTORY_BOOL_FILTER_PARAMS) {
    if (sp.get(param) === '1') filters[key] = true;
  }
  const sortParam = sp.get('sort');
  const sortMode = sortParam && SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : 'recommended';
  // Paging depth: only a depth beyond the first page means anything.
  const show = Number(sp.get('show'));
  const visibleCount = Number.isInteger(show) && show > 30 ? show : 30;
  return {
    query: sp.get('q') ?? '',
    viewMode: sp.get('view') ?? 'all',
    filters,
    sortMode,
    visibleCount,
  };
}

/** The URL for a search state: `prev` with the search's own parameters
 *  rewritten and every other parameter (map, near) left as it was. Each
 *  default is left out, so a fresh directory has a bare URL. */
export function writeSearchState(prev: URLSearchParams, state: DirectorySearchState): URLSearchParams {
  const sp = new URLSearchParams(prev);
  if (state.query) sp.set('q', state.query);
  else sp.delete('q');
  if (state.viewMode !== 'all') sp.set('view', state.viewMode);
  else sp.delete('view');
  for (const key of DIRECTORY_LIST_FILTER_KEYS) {
    const values = state.filters[key] ?? [];
    if (values.length > 0) sp.set(key, encodeListParam(values));
    else sp.delete(key);
  }
  for (const [key, param] of DIRECTORY_BOOL_FILTER_PARAMS) {
    if (state.filters[key]) sp.set(param, '1');
    else sp.delete(param);
  }
  if (state.sortMode && state.sortMode !== 'recommended') sp.set('sort', state.sortMode);
  else sp.delete('sort');
  if (state.visibleCount > 30) sp.set('show', String(state.visibleCount));
  else sp.delete('show');
  return sp;
}
